import React, { useCallback, useRef, useState, useEffect } from 'react'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import QuizOptions from '../shared/QuizOptions'
import QuizOption from '../shared/QuizOption'
import { getQuizOptionsData } from '../../constants'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackStyleSelected } from '../../utils/analytics'
import { useVariant } from '../../contexts/VariantContext'
import quizMainRealistic from '../../assets/quiz/realistic/main.mp4'
import quizMainAnime from '../../assets/quiz/anime/main.mp4'

const Step0 = ({ onAnswer }) => {
  const location = useLocation()
  const isQuiz = !!location.pathname.includes('/quiz')
  const isQuizOnly = isQuiz && !location.pathname.includes('/quiz-sfw')
  const [selectedOption, setSelectedOption] = useState('Realistic') // Default to first option
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const videoRefs = useRef([])
  const [isMobile, setIsMobile] = useState(false)

  // Get quiz options based on selected style
  const quizOptions = getQuizOptionsData('welcome', selectedOption)

  const videos = [
    isQuiz
      ? quizMainRealistic
      : 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Realistic/realistic_createsexfriend_2.mp4',
    isQuiz
      ? quizMainAnime
      : 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Anime/anime_createsexfriend.mp4',
  ]

  // Poster images for mobile - now dynamic based on selected style
  const posterImages = [
    isQuiz
      ? undefined
      : 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Realistic/step0-image.jpg',
    isQuiz
      ? undefined
      : 'https://cdnsm.swipey.ai/prelander/createsexfriend-white/Anime/step0-image.jpg',
  ]

  // Detect mobile devices
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase()
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    setIsMobile(isMobileDevice)
  }, [])

  // Simplified video playback logic
  const playVideo = useCallback(index => {
    const video = videoRefs.current[index]
    if (video && video.paused) {
      console.log(`Playing video ${index}`)
      video.currentTime = 0
      video.play().catch(console.warn)
    }
  }, [])

  const stopVideo = useCallback(index => {
    const video = videoRefs.current[index]
    if (video && !video.paused) {
      console.log(`Stopping video ${index}`)
      video.pause()
      video.currentTime = 0
    }
  }, [])

  // Handle option hover (desktop only)
  const handleOptionHover = useCallback(
    (index, isHovering) => {
      if (!isMobile) {
        console.log(`Hover ${index}: ${isHovering}`)
        setHoveredIndex(isHovering ? index : null)

        if (isHovering) {
          // Start playing video on hover (desktop only)
          playVideo(index)
        } else {
          // Stop video on hover end (unless it's selected)
          if (index !== quizOptions.findIndex(opt => opt.value === selectedOption)) {
            stopVideo(index)
          }
        }
      }
    },
    [playVideo, stopVideo, selectedOption, quizOptions, isMobile],
  )

  // Handle option click
  const handleOptionClick = useCallback(
    (value, index) => {
      console.log(`Clicking option: ${value}`)
      setSelectedOption(value)

      // Stop all videos first
      videoRefs.current.forEach((video, i) => {
        if (video) {
          video.pause()
          video.currentTime = 0
        }
      })

      // Start playing the selected video
      setTimeout(() => {
        playVideo(index)
      }, 100)
    },
    [playVideo],
  )

  // Initialize videos when component mounts
  useEffect(() => {
    videoRefs.current.forEach((videoRef, index) => {
      if (videoRef) {
        // Set video properties
        videoRef.muted = true
        videoRef.loop = true
        videoRef.playsInline = true
        videoRef.preload = 'metadata'
        videoRef.controls = false

        // Hide all controls
        videoRef.style.webkitMediaControls = 'none'
        videoRef.style.webkitMediaControlsOverlayPlayButton = 'none'
        videoRef.style.webkitMediaControlsPlayButton = 'none'
        videoRef.style.webkitMediaControlsStartPlaybackButton = 'none'
        videoRef.style.webkitMediaControlsTimeline = 'none'
        videoRef.style.webkitMediaControlsVolumeSlider = 'none'
        videoRef.style.webkitMediaControlsMuteButton = 'none'
        videoRef.style.webkitMediaControlsFullscreenButton = 'none'

        // Remove controls attributes
        videoRef.removeAttribute('controls')
        videoRef.removeAttribute('controlsList')

        // Add event listeners for debugging
        videoRef.addEventListener('play', () => console.log(`Video ${index} started playing`))
        videoRef.addEventListener('pause', () => console.log(`Video ${index} paused`))
      }
    })

    // Start playing the default selected video after a short delay
    setTimeout(() => {
      const defaultIndex = quizOptions.findIndex(opt => opt.value === selectedOption)
      if (defaultIndex !== -1) {
        console.log(`Starting default video: ${defaultIndex}`)
        playVideo(defaultIndex)
      }
    }, 500)
  }, [selectedOption, quizOptions, playVideo])

  const handleContinue = () => {
    if (selectedOption !== null) {
      trackStyleSelected(selectedOption)
      onAnswer('welcome', selectedOption)
    } else {
      alert('Please select an option before continuing')
    }
  }

  const canContinue = selectedOption
  const { t } = useTranslation()
  const variant = useVariant()
  return (
    <div className='quizContentWrapper quizContentWrapperStep0'>
      <QuizContent className='mainStep'>
        <QuizTitle>
          {isQuiz ? (
            <>
              Create
              <span className='specialText'> your companion</span>
            </>
          ) : variant?.titleParts ? (
            <>
              {variant.titleParts.action}
              <span className='specialText'> {variant.titleParts.middle} </span>
              {variant.titleParts.object}
            </>
          ) : (
            <>
              {t('create')}
              <span className='specialText'> {t('your_own')} </span>
              {t('ai_girlfriend')}
            </>
          )}
        </QuizTitle>
        {isQuizOnly && (
          <p className='quiz-companion-description'>
            Swipey AI lets you build and customize your own AI companion, offering a new kind of experience in your daily life. Chat 24/7 on any topic, hop on voice calls, and explore what different kinds of connection can feel like — on your own terms.
          </p>
        )}
        <div className='step0Wrapper'>
          <QuizOptions className='step0-options'>
            {quizOptions.map((option, index) => {
              const isSelected = selectedOption === option.value
              const isHovered = hoveredIndex === index

              return (
                <QuizOption
                  key={option.value}
                  value={option.value}
                  name='welcome'
                  onClick={() => handleOptionClick(option.value, index)}
                  onMouseEnter={() => handleOptionHover(index, true)}
                  onMouseLeave={() => handleOptionHover(index, false)}
                  className={`step0-option ${isSelected ? 'selected' : selectedOption !== null ? 'not-selected' : ''}`}
                >
                  <div className='option-image'>
                    <video
                      ref={el => (videoRefs.current[index] = el)}
                      className='step0Video'
                      muted
                      loop
                      autoPlay
                      src={videos[index]}
                      poster={posterImages[index]}
                      preload='auto'
                      playsInline
                      webkit-playsinline='true'
                      x5-playsinline='true'
                      alt={option.alt}
                      controls={false}
                      disablePictureInPicture
                      controlsList='nodownload nofullscreen noremoteplayback'
                    />
                    <div className='step0-image-title'>{t(option.text)}</div>
                  </div>
                </QuizOption>
              )
            })}
          </QuizOptions>

          <button
            disabled={!canContinue}
            className={`continue-button step0 new-design-button${selectedOption ? ' option-selected' : ''}`}
            onClick={handleContinue}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default Step0
